import { AlarmClock, AlarmClockOff, CalendarClock } from "lucide-react";
import { DateTime } from "luxon";
import { type ReactNode, useState } from "react";
import { Button } from "@/components/ui/button";
import { DateTimePickerPanel } from "@/components/ui/datetime-picker";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { formatDate } from "@/lib/utils";

export interface SnoozePreset {
  label: string;
  /** Muted right-aligned context, e.g. "Wed, 9 AM" */
  hint: string;
  until: Date;
}

/** Gmail-style wake times: later today, then 9 AM on meaningful days. */
export function getSnoozePresets(): SnoozePreset[] {
  const now = DateTime.now();
  const at9 = (day: DateTime) => day.set({ hour: 9, minute: 0, second: 0, millisecond: 0 });

  const presets: SnoozePreset[] = [];

  const laterToday = now.plus({ hours: 3 }).startOf("hour");
  if (laterToday.hasSame(now, "day")) {
    presets.push({
      label: "Later today",
      hint: laterToday.toFormat("h a"),
      until: laterToday.toJSDate(),
    });
  }

  const tomorrow = at9(now.plus({ days: 1 }));
  presets.push({ label: "Tomorrow", hint: tomorrow.toFormat("ccc, h a"), until: tomorrow.toJSDate() });

  // Saturday morning; skip when it's already the weekend.
  const saturday = at9(now.startOf("week").plus({ days: 5 }));
  if (saturday > now.plus({ days: 1 }).endOf("day")) {
    presets.push({
      label: "This weekend",
      hint: saturday.toFormat("ccc, h a"),
      until: saturday.toJSDate(),
    });
  }

  const nextMonday = at9(now.startOf("week").plus({ weeks: 1 }));
  presets.push({
    label: "Next week",
    hint: nextMonday.toFormat("ccc, h a"),
    until: nextMonday.toJSDate(),
  });

  return presets;
}

export interface SnoozeControlProps {
  /** When set, the menu shows the wake date and offers Unsnooze. */
  snoozedUntil?: string;
  onSnooze: (until: Date) => void;
  onUnsnooze?: () => void;
  trigger: ReactNode;
  /** Optional tooltip shown on hover/focus of the trigger. */
  tooltip?: ReactNode;
  align?: "start" | "center" | "end";
}

/**
 * First-class snooze: preset wake times (Gmail-style) plus a custom
 * date/time step, in a single popover. Hosts decide what the trigger looks
 * like (row icon, drawer button, badge menu item).
 */
export function SnoozeControl({
  snoozedUntil,
  onSnooze,
  onUnsnooze,
  trigger,
  tooltip,
  align = "end",
}: SnoozeControlProps) {
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState(false);

  // Computed at open so the presets are fresh.
  const presets = open ? getSnoozePresets() : [];

  function commit(until: Date) {
    onSnooze(until);
    setOpen(false);
  }

  // Tooltip wraps the popover trigger (not vice versa) so both Radix
  // primitives forward their props down to the host's trigger element —
  // same composition as DateTimePicker's tooltip.
  const popoverTrigger = <PopoverTrigger asChild>{trigger}</PopoverTrigger>;

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setCustom(false);
      }}
    >
      {tooltip ? (
        <TooltipProvider>
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>{popoverTrigger}</TooltipTrigger>
            <TooltipContent side="top" className="text-xs">
              {tooltip}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        popoverTrigger
      )}
      <PopoverContent
        align={align}
        className="w-[240px] overflow-hidden p-0"
        onClick={(e) => e.stopPropagation()}
      >
        {custom ? (
          <DateTimePickerPanel
            value={snoozedUntil ? new Date(snoozedUntil) : null}
            onChange={(date) => {
              if (date) commit(date);
            }}
            minDate={new Date()}
            reviewTitle="Snooze until"
            autoFocus
            onConfirm={() => setOpen(false)}
          />
        ) : (
          <div className="flex flex-col gap-0.5 p-2">
            <p className="px-2 pb-1 pt-0.5 text-xs font-medium text-muted-foreground">
              {snoozedUntil
                ? `Snoozed until ${formatDate(snoozedUntil, "MMM d, h:mm a")}`
                : "Snooze until..."}
            </p>
            {presets.map((preset) => (
              <Button
                key={preset.label}
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 justify-between gap-3 px-2 font-normal"
                onClick={() => commit(preset.until)}
              >
                <span className="flex items-center gap-2">
                  <AlarmClock className="size-3.5 text-muted-foreground" aria-hidden="true" />
                  {preset.label}
                </span>
                <span className="text-xs text-muted-foreground">{preset.hint}</span>
              </Button>
            ))}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 justify-start gap-2 px-2 font-normal"
              onClick={() => setCustom(true)}
            >
              <CalendarClock className="size-3.5 text-muted-foreground" aria-hidden="true" />
              Pick date & time...
            </Button>
            {snoozedUntil && onUnsnooze ? (
              <>
                <div className="my-1 h-px bg-border" />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 justify-start gap-2 px-2 font-normal"
                  onClick={() => {
                    onUnsnooze();
                    setOpen(false);
                  }}
                >
                  <AlarmClockOff className="size-3.5 text-muted-foreground" aria-hidden="true" />
                  Unsnooze
                </Button>
              </>
            ) : null}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
